import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useSupabase, useTenant } from "@how/app-kit";
import { formatDateOnly } from "@how/ui";
import type { Enums } from "@how/database";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type ServeResponse = Enums<"serve_response">;
export type SeasonStatus = Enums<"service_season_status">;

export const RESPONSE_LABEL: Record<ServeResponse, string> = {
  no_reply: "No reply",
  accepted: "Accepted",
  declined: "Declined",
};
export const SEASON_STATUS_LABEL: Record<SeasonStatus, string> = {
  draft: "Draft",
  requested: "Requests sent",
  closed: "Closed",
};

/** A plan's date and time in the reader's locale, short: "Sun 4 Oct, 9:00 AM". */
export function formatPlanWhen(iso: string, timeZone?: string | null): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * A season's window, short: "4 Oct to 22 Nov 2026". Its dates are calendar days (a Postgres `date`), so they go through
 * `formatDateOnly`: `new Date("2026-10-04")` is midnight UTC, the evening before anywhere in the Americas (#397).
 */
export function formatSeasonDates(startsOn: string, endsOn: string): string {
  const start = formatDateOnly(startsOn, undefined, { day: "numeric", month: "short" });
  const end = formatDateOnly(endsOn, undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${start} to ${end}`;
}

export interface Season {
  readonly id: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly status: SeasonStatus;
  readonly requestedAt: string | null;
  /** When it was archived: out of the picker and the member's schedule, its history kept (#321). */
  readonly archivedAt: string | null;
}

/** Today in the reader's zone, as a season's dates are written: "2026-10-02". */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The season the page opens on (#321): of those not archived, the one running today (the latest to start, if two
 * overlap), else the next to start, else the last to end. Null when every season is archived or there are none.
 */
export function defaultSeason(seasons: readonly Season[], today: string): Season | null {
  const open = seasons.filter((s) => s.archivedAt === null);
  const latestStart = (a: Season, b: Season) => (a.startsOn >= b.startsOn ? a : b);
  const current = open.filter((s) => s.startsOn <= today && today <= s.endsOn);
  if (current.length > 0) return current.reduce(latestStart);
  const next = open.filter((s) => s.startsOn > today);
  if (next.length > 0) return next.reduce((a, b) => (a.startsOn <= b.startsOn ? a : b));
  if (open.length > 0) return open.reduce((a, b) => (a.endsOn >= b.endsOn ? a : b));
  return null;
}

export function useSeasons() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["service-seasons", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<Season[]> => {
      const { data, error } = await supabase
        .from("service_seasons")
        .select("id, name, starts_on, ends_on, status, requested_at, archived_at")
        .eq("tenant_id", tenantId ?? "")
        .order("starts_on", { ascending: false });
      if (error) throw error;
      return data.map((s) => ({
        id: s.id,
        name: s.name,
        startsOn: s.starts_on,
        endsOn: s.ends_on,
        status: s.status,
        requestedAt: s.requested_at,
        archivedAt: s.archived_at,
      }));
    },
  });
}

export interface ServePosition {
  readonly id: string;
  readonly name: string;
  readonly teamId: string;
  readonly teamName: string;
  readonly sortOrder: number;
  readonly isActive: boolean;
}

/** Every position on every serve team, active teams first; the grid's rows and the Positions dialog. */
export function useServePositions() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["serve-positions", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ServePosition[]> => {
      const { data, error } = await supabase
        .from("serve_positions")
        .select("id, name, team_id, sort_order, is_active, serve_teams(name)")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data
        .map((p) => ({
          id: p.id,
          name: p.name,
          teamId: p.team_id,
          teamName: p.serve_teams.name,
          sortOrder: p.sort_order,
          isActive: p.is_active,
        }))
        .sort((a, b) => a.teamName.localeCompare(b.teamName) || a.sortOrder - b.sortOrder);
    },
  });
}

export function useServeTeams() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["serve-teams", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const { data, error } = await supabase
        .from("serve_teams")
        .select("id, name")
        .eq("tenant_id", tenantId ?? "")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });
}

export interface TeamMember {
  readonly personId: string;
  readonly name: string;
}

/** The active members of a team the grid may assign; a child the reader may not see is not listed (ADR 0028). */
export function useTeamMembers(teamId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["serve-team-members", tenantId, teamId],
    enabled: tenantId !== null && teamId !== null,
    queryFn: async (): Promise<TeamMember[]> => {
      const { data, error } = await supabase
        .from("serve_team_memberships")
        .select("person_id, people(first_name, last_name)")
        .eq("tenant_id", tenantId ?? "")
        .eq("team_id", teamId ?? "")
        .eq("status", "active");
      if (error) throw error;
      return data
        .map((m) => ({
          personId: m.person_id,
          name: `${m.people.first_name} ${m.people.last_name}`.trim(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

const assignmentSchema = z.object({
  id: z.string(),
  personId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  response: z.enum(["no_reply", "accepted", "declined"]),
  respondedAt: z.string().nullable(),
  note: z.string().nullable(),
  requested: z.boolean(),
  conflict: z.boolean(),
});
const gridSchema = z.object({
  season: z.object({
    id: z.string(),
    name: z.string(),
    startsOn: z.string(),
    endsOn: z.string(),
    status: z.enum(["draft", "requested", "closed"]),
    requestedAt: z.string().nullable(),
  }),
  plans: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      startsAt: z.string(),
      endsAt: z.string(),
      isCancelled: z.boolean(),
      campus: z.string().nullable(),
      campusTimeZone: z.string().nullable(),
      positions: z.array(
        z.object({
          planPositionId: z.string(),
          positionId: z.string(),
          position: z.string(),
          team: z.string(),
          needCount: z.number(),
          hiddenCount: z.number(),
          assignments: z.array(assignmentSchema),
        }),
      ),
    }),
  ),
  load: z.array(
    z.object({
      personId: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      assignments: z.number(),
      accepted: z.number(),
      declined: z.number(),
    }),
  ),
});
export type SeasonGrid = z.infer<typeof gridSchema>;
export type GridPlan = SeasonGrid["plans"][number];
export type GridSlot = GridPlan["positions"][number];
export type GridAssignment = GridSlot["assignments"][number];

/** The season as the grid draws it, from one call (ADR 0043 §5). */
export function useSeasonGrid(seasonId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["season-grid", tenantId, seasonId],
    enabled: tenantId !== null && seasonId !== null,
    queryFn: async (): Promise<SeasonGrid> => {
      const { data, error } = await supabase.rpc("season_grid", { p_season_id: seasonId ?? "" });
      if (error) throw error;
      return gridSchema.parse(data);
    },
  });
}

/** The only words a scheduler ever sees about another church (spec section 8, ADR 0066). */
export const BUSY_ELSEWHERE = "Busy at another church at this time";

/** Not allowed to ask is the same as nothing to say: no warning, no error toast. */
function isRefused(error: { code?: string }): boolean {
  return error.code === "42501";
}

/** Whether this person is busy at another church during this plan; false unless they said yes. */
export function usePersonBusyElsewhere(planId: string | null, personId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["busy-elsewhere", tenantId, planId, personId],
    enabled: tenantId !== null && planId !== null && personId !== null,
    staleTime: 0,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("person_busy_elsewhere", {
        p_plan_id: planId ?? "",
        p_person_id: personId ?? "",
      });
      if (error) {
        if (isRefused(error)) return false;
        throw error;
      }
      return data;
    },
  });
}

/** The season's assignments whose people are busy at another church then (ids only). */
export function useSeasonBusyElsewhere(seasonId: string | null, enabled: boolean) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["season-busy-elsewhere", tenantId, seasonId],
    enabled: enabled && tenantId !== null && seasonId !== null,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase.rpc("season_busy_elsewhere", {
        p_season_id: seasonId ?? "",
      });
      if (error) {
        if (isRefused(error)) return new Set<string>();
        throw error;
      }
      return new Set(data);
    },
  });
}

function useServicesInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["season-grid", tenantId] });
    await client.invalidateQueries({ queryKey: ["service-seasons", tenantId] });
    await client.invalidateQueries({ queryKey: ["serve-positions", tenantId] });
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    await client.invalidateQueries({ queryKey: ["season-busy-elsewhere", tenantId] });
  };
}

export interface SeasonFields {
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
}

export function useSaveSeason() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { seasonId: string | null; fields: SeasonFields }) => {
      const { data, error } = await supabase.rpc("set_service_season", {
        p_tenant_id: tenantId ?? "",
        p_name: input.fields.name,
        p_starts_on: input.fields.startsOn,
        p_ends_on: input.fields.endsOn,
        ...(input.seasonId === null ? {} : { p_season_id: input.seasonId }),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useCloseSeason() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (seasonId: string) => {
      const { error } = await supabase.rpc("close_service_season", { p_season_id: seasonId });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

/** Archive a season, or restore it (#321). */
export function useArchiveSeason() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { seasonId: string; archived: boolean }) => {
      const { error } = await supabase.rpc("set_service_season_archived", {
        p_season_id: input.seasonId,
        p_archived: input.archived,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

/**
 * Delete a season made by mistake, with its plans (#321). The database refuses once a request went out or an answer
 * was recorded (23514), and for a login limited to campuses (42501). Returns how many plans went with it.
 */
export function useDeleteSeason() {
  const supabase = useSupabase();
  const client = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (seasonId: string) => {
      const { data, error } = await supabase.rpc("delete_service_season", {
        p_season_id: seasonId,
      });
      if (error) throw error;
      return data;
    },
    // The list first, so the page moves off the season; its grid is then dropped, not asked for again (it would be
    // "not found").
    onSuccess: async (_plans, seasonId) => {
      await client.invalidateQueries({ queryKey: ["service-seasons", tenantId] });
      client.removeQueries({ queryKey: ["season-grid", tenantId, seasonId] });
      await client.invalidateQueries({ queryKey: ["person", tenantId] });
    },
  });
}

/** Why a season was not deleted, in the church's words: the database's 42501 and 23514 (#321). */
export function deleteRefusal(error: unknown, name: string): string {
  const code = typeof error === "object" && error !== null && "code" in error ? error.code : null;
  if (code === "42501")
    return `${name} was not deleted: your account is limited to campuses, and a season covers every campus. An owner or administrator can delete it.`;
  if (code === "23514")
    return `${name} was not deleted: someone has been asked or has answered since. Archive it instead.`;
  return `${name} was not deleted. Check your connection and try again.`;
}

/**
 * A grid action the database refused, in words (#403): 42501 is the role (or a login limited to other campuses), a
 * broken rule (23514) or a clash (23505) gives the database's own words, P0002 means it changed or went meanwhile, and
 * anything else is the connection.
 */
export function gridRefusal(what: string, error: unknown): string {
  const e = (typeof error === "object" && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
  };
  const words = typeof e.message === "string" ? e.message.trim() : "";
  if (e.code === "42501")
    return `${what}: your role doesn't allow it, or your account is limited to other campuses.`;
  if ((e.code === "23514" || e.code === "23505") && words !== "") return `${what}: ${words}.`;
  if (e.code === "P0002")
    return `${what}: it was changed or removed by someone else. Reload the page to see the latest.`;
  return `${what}. Check your connection and try again.`;
}

export interface PlanFields {
  readonly title: string;
  /** Local wall-clock start, as the datetime-local input gives it. */
  readonly startsAt: string;
  readonly minutes: number;
  readonly campusId: string | null;
  readonly notes: string;
}

export function useSavePlan() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { planId: string | null; seasonId: string; fields: PlanFields }) => {
      const starts = new Date(input.fields.startsAt);
      const ends = new Date(starts.getTime() + input.fields.minutes * 60_000);
      const { data, error } = await supabase.rpc("set_service_plan", {
        p_tenant_id: tenantId ?? "",
        p_title: input.fields.title,
        p_starts_at: starts.toISOString(),
        p_ends_at: ends.toISOString(),
        ...(input.planId === null ? {} : { p_plan_id: input.planId }),
        p_season_id: input.seasonId,
        ...(input.fields.campusId === null ? {} : { p_campus_id: input.fields.campusId }),
        ...(input.fields.notes.trim() === "" ? {} : { p_notes: input.fields.notes }),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useCancelPlan() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { planId: string; isCancelled: boolean }) => {
      const { error } = await supabase.rpc("cancel_service_plan", {
        p_plan_id: input.planId,
        p_is_cancelled: input.isCancelled,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useSavePosition() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: {
      positionId: string | null;
      teamId: string;
      name: string;
      sortOrder: number;
    }) => {
      const { error } = await supabase.rpc("set_serve_position", {
        p_tenant_id: tenantId ?? "",
        p_team_id: input.teamId,
        p_name: input.name,
        ...(input.positionId === null ? {} : { p_position_id: input.positionId }),
        p_sort_order: input.sortOrder,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useSetPositionActive() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { positionId: string; isActive: boolean }) => {
      const { error } = await supabase.rpc("set_serve_position_active", {
        p_position_id: input.positionId,
        p_is_active: input.isActive,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useSetPlanPosition() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { planId: string; positionId: string; needCount: number }) => {
      const { error } = await supabase.rpc("set_plan_position", {
        p_plan_id: input.planId,
        p_position_id: input.positionId,
        p_need_count: input.needCount,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useRemovePlanPosition() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (planPositionId: string) => {
      const { error } = await supabase.rpc("remove_plan_position", {
        p_plan_position_id: planPositionId,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useAssignPerson() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { planPositionId: string; personId: string }) => {
      const { error } = await supabase.rpc("assign_person", {
        p_plan_position_id: input.planPositionId,
        p_person_id: input.personId,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUnassignPerson() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase.rpc("unassign_person", { p_assignment_id: assignmentId });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

/** Staff answering for a person: a word in the foyer, a phone call (ADR 0043 §6). */
export function useRespondFor() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { assignmentId: string; accepted: boolean; note?: string }) => {
      const { error } = await supabase.rpc("respond_to_assignment", {
        p_assignment_id: input.assignmentId,
        p_accepted: input.accepted,
        ...(input.note === undefined ? {} : { p_note: input.note }),
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

/** One request per person for every date not yet asked (ADR 0043 §3). Returns how many people were asked. */
export function useSendRequests() {
  const supabase = useSupabase();
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: async (input: { seasonId: string; message: string }) => {
      const { data, error } = await supabase.rpc("send_season_requests", {
        p_season_id: input.seasonId,
        ...(input.message.trim() === "" ? {} : { p_message: input.message }),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(),
  });
}
